import {
  computeWeightedSubscriptionPercentage,
  roundSubscriptionPercentage,
  toSubscriptionPercentage,
} from './formatters';

export interface SubscriptionTableCellData {
  percentage: number | null;
  factureCount: number;
}

type SubscriptionBreakdownItem = {
  subscription_percentage?: number;
  facture_count?: number | null;
};

const computeWeightedFromRawPercentages = (
  items: Array<{ percentage: number | null | undefined; factureCount: number | null | undefined }>
): number | null => {
  let weightedSum = 0;
  let weightTotal = 0;

  items.forEach(({ percentage, factureCount }) => {
    if (percentage != null && factureCount != null && factureCount > 0) {
      weightedSum += percentage * factureCount;
      weightTotal += factureCount;
    }
  });

  return weightTotal > 0 ? roundSubscriptionPercentage(weightedSum / weightTotal) : null;
};

export function groupSubscriptionMonthItems<T extends SubscriptionBreakdownItem>(
  monthItems: T[],
  getCategory: (item: T) => string
): Map<string, SubscriptionTableCellData> {
  const grouped = new Map<string, Array<{ percentage: number | null | undefined; factureCount: number }>>();

  monthItems.forEach(item => {
    const category = getCategory(item);
    const factureCount = item.facture_count ?? 0;
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category)!.push({
      percentage: item.subscription_percentage,
      factureCount,
    });
  });

  const result = new Map<string, SubscriptionTableCellData>();
  grouped.forEach((entries, category) => {
    const factureCount = entries.reduce((sum, entry) => sum + entry.factureCount, 0);
    result.set(category, {
      percentage: computeWeightedFromRawPercentages(entries),
      factureCount,
    });
  });

  return result;
}

export function computeSubscriptionMonthTotal(
  monthItems: SubscriptionBreakdownItem[]
): SubscriptionTableCellData {
  const entries = monthItems.map(item => ({
    percentage: item.subscription_percentage,
    factureCount: item.facture_count ?? 0,
  }));

  return {
    percentage: computeWeightedFromRawPercentages(entries),
    factureCount: entries.reduce((sum, entry) => sum + entry.factureCount, 0),
  };
}

export function toSubscriptionTableCell(
  groupedValue: SubscriptionTableCellData | undefined
): { percentage: number | null; factureCount: number | null } {
  if (!groupedValue) {
    return { percentage: null, factureCount: null };
  }

  return {
    percentage: toSubscriptionPercentage(groupedValue.percentage),
    factureCount: groupedValue.factureCount,
  };
}

// Kept for backward compatibility inside table row builders that already pass normalized values.
export { computeWeightedSubscriptionPercentage };
